import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@webapp/auth';
import { IProspectRegister } from '@webapp/auth/interfaces/register.interface';
import { getGrossIncomeConditionKey, IUser } from '@webapp/users/interfaces/users.interface';
import { UserUpdateFormComponent } from '@webapp/users';

@Component({
  selector: 'app-prospect-signup-step',
  standalone: true,
  imports: [UserUpdateFormComponent],
  templateUrl: './prospect-signup-step.component.html',
  styleUrl: './prospect-signup-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProspectSignupStepComponent {
  private readonly authService = inject(AuthService);

  user = input.required<IUser>();
  signupSuccess = output<IUser>();

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);

  async onUserUpdated(updated: IUser): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);

    const isPerson = updated.type?.toUpperCase() !== 'COMPANY';

    const baseEntity = {
      email: updated.email,
      phone: updated.phone,
      address: updated.address,
      postalCode: updated.postalCode,
      zoneId: updated.zoneId,
      role: 'PROSPECT',
    };

    const entityFields = isPerson
      ? { firstName: updated.firstName, lastName: updated.lastName, birthDate: updated.birthDate ?? undefined }
      : { businessName: updated.companyName };

    const payload: IProspectRegister = {
      type: 'PROSPECT',
      entity: { ...baseEntity, ...entityFields },
      identification: {
        type: isPerson ? 'DNI' : 'CUIT',
        value: updated.identification ?? '',
        grossIncomeCondition: getGrossIncomeConditionKey(updated.grossIncomeCondition),
      },
    };

    try {
      await firstValueFrom(this.authService.registerProspect(payload));
      this.signupSuccess.emit(updated);
    } catch (error) {
      console.error('Error en sign-up de prospecto:', error);
      this.errorMessage.set('No pudimos registrarte. Intentá nuevamente.');
    } finally {
      this.loading.set(false);
    }
  }
}
